import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';

import { SYS4090_R01Dto } from './Dto_SYS4090_R01';
import { useSYS4090_R01Store } from './Store_SYS4090_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SYS4090_R01-grid-column-state';

export interface Props {
	onRowClick?: (rowData: SYS4090_R01Dto) => void;
	gridRef: React.RefObject<AgGridReact<SYS4090_R01Dto> | null>;
	defaultSYS4090_R01Row: SYS4090_R01Dto;
}

export const Table_SYS4090_R01 = (props: Props) => {
	const { gridRef, defaultSYS4090_R01Row } = props;

	const {
		SYS4090_R01List,
		selectedSYS4090_R01,
		setSelectedSYS4090_R01,
		initSelectedSYS4090_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useSYS4090_R01Store();

	const { interesttypeList } = useCommonCodeStore();

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'bankAcctCd',
		gridData: SYS4090_R01List,
		defaultGridData: defaultSYS4090_R01Row,
		selectedRow: selectedSYS4090_R01,
	});

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const headerNames: Record<string, string> = {
		rowIndex: '',
		bankAcctCd: '계좌코드',
		bankAcctNm: '계좌명',
		bankAcctNo: '실제 계좌 번호',
		acctTypeNm: '예적금구분',
		bankBalance: '잔액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'bankAcctCd')(params), []);

	const baseColumnDefs: ColDef<SYS4090_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '계좌코드', colId: 'bankAcctCd', field: 'bankAcctCd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bankAcctCd'),
		},
		{
			headerName: '계좌명', colId: 'bankAcctNm', field: 'bankAcctNm', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bankAcctNm'),
		},
		{
			headerName: '실제 계좌 번호', colId: 'bankAcctNo', field: 'bankAcctNo', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bankAcctNo'),
		},
		{
			headerName: '예적금구분', colId: 'acctType', field: 'acctTypeNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'acctTypeNm'),
		},
		{
			headerName: '잔액', colId: 'bankBalance', field: 'bankBalance', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bankBalance'),
			valueFormatter: (p) => p.value != null ? p.value.toLocaleString() : '',
		},
		{
			headerName: '이자유형', colId: 'interesttype', field: 'interesttype', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'interesttype'),
			...CommonCodeColumn.options(interesttypeList, true, undefined, undefined),
		},
		{
			headerName: '이자율', colId: 'interestrate', field: 'interestrate', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'interestrate'),
		},
		{
			headerName: '할부금액', colId: 'installmentAmt', field: 'installmentAmt', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'installmentAmt'),
		},
	], [getCellStyle, interesttypeList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SYS4090_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SYS4090_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SYS4090_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedSYS4090_R01?.bankAcctCd === rowData.bankAcctCd;
		setSelectedSYS4090_R01(isSelected ? undefined : rowData);
	}, [selectedSYS4090_R01, setSelectedSYS4090_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		bankAcctCd: `${SYS4090_R01List.length.toLocaleString()}건`,
		bankBalance: SYS4090_R01List.reduce(
			(sum, account) => sum + (account.bankBalance || 0), 0,
		).toLocaleString(),
		editable: false,
	}]), [SYS4090_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SYS4090_R01List, selectedSYS4090_R01]);

	return (
		<GridContextWrapper<SYS4090_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS4090_R01}
			defaultRowData={defaultSYS4090_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SYS4090_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SYS4090_R01Dto>({
					idKey: 'bankAcctCd',
					selectedList: selectedSYS4090_R01 ? [selectedSYS4090_R01.bankAcctCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default Table_SYS4090_R01;
